import { Link, Navigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';
import { SITE_ORIGIN } from '@/site';
import { BLOG_POSTS } from '@/components/data/blogPosts';
import { BlogLayout } from './BlogLayout';

export const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useSEO({
    title: post ? `${post.title} | Playano Education` : 'Post Not Found | Playano Education',
    description: post ? post.excerpt : 'Blog post not found.',
    keywords: post ? post.keywords.join(', ') : '',
    ogTitle: post ? post.title : 'Playano Engineering Blog',
    ogDescription: post ? post.excerpt : 'Playano Engineering Blog',
    ogType: 'article',
    canonicalUrl: post ? `${SITE_ORIGIN}/blog/${post.slug}` : `${SITE_ORIGIN}/blog`,
  });

  if (!post) {
    return <Navigate to="/" replace />;
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <BlogLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
          <a href="https://playanoedu.com/" className="hover:text-white transition-colors">
            Playano Education
          </a>
          <span aria-hidden="true" className="text-gray-700">
            /
          </span>
          <Link to="/" className="inline-flex items-center gap-1 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Blog
          </Link>
          <span aria-hidden="true" className="text-gray-700">
            /
          </span>
          <span className="text-gray-400 truncate max-w-[60vw]">{post.title}</span>
        </nav>

        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{post.series}</p>
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2.5 py-1 rounded-full bg-[#FD9800]/10 border border-[#FD9800]/30 text-[#FD9800] text-xs font-semibold">
              {post.category}
            </span>
            <span className="text-sm text-gray-500">
              {new Date(post.date + 'T00:00:00Z').toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{post.title}</h1>
          <p className="text-lg text-gray-400">{post.excerpt}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 [&>ul]:list-disc [&>ul]:pl-6 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-10 [&_h2]:mb-2 [&_p]:text-gray-300 [&_li]:text-gray-300">
          {post.content}
        </div>

        {post.faq && post.faq.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-4">FAQ</h2>
            <div className="space-y-4">
              {post.faq.map((f, i) => (
                <div key={i} className="rounded-xl border border-[#333] p-4 bg-[#171717]">
                  <h3 className="font-semibold text-white">{f.q}</h3>
                  <p className="mt-1 text-sm text-gray-400">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {post.sources && (
          <div className="mt-10">
            <h2 className="text-2xl font-bold text-white mb-4">Sources</h2>
            <p className="text-sm text-gray-500 leading-relaxed">{post.sources}</p>
          </div>
        )}

        {/* JSON-LD structured data — BlogPosting + FAQPage (AEO/GEO lever) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'BlogPosting',
                  headline: post.title,
                  description: post.excerpt,
                  datePublished: post.date,
                  keywords: post.keywords.join(', '),
                  image: `${SITE_ORIGIN}${post.image}`,
                  url: `${SITE_ORIGIN}/blog/${post.slug}`,
                  author: { '@type': 'Organization', name: 'Playano Education', url: SITE_ORIGIN },
                  publisher: { '@type': 'Organization', name: 'Playano Education', url: SITE_ORIGIN },
                },
                ...(post.faq && post.faq.length > 0
                  ? [
                      {
                        '@type': 'FAQPage',
                        mainEntity: post.faq.map((f) => ({
                          '@type': 'Question',
                          name: f.q,
                          acceptedAnswer: { '@type': 'Answer', text: f.a },
                        })),
                      },
                    ]
                  : []),
              ],
            }),
          }}
        />

        <div className="mt-12 pt-8 border-t border-[#333]">
          <p className="text-sm text-gray-500 mb-4">Keep reading</p>
          <div className="grid gap-6 sm:grid-cols-2">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                className="group bg-[#171717] rounded-xl border border-[#333] hover:border-[#FD9800] transition-all overflow-hidden flex flex-col"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-40 object-cover group-hover:scale-[1.03] transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#FD9800]">
                      {p.category}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(p.date + 'T00:00:00Z').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC',
                      })}
                    </span>
                  </div>
                  <h3 className="font-semibold text-white group-hover:text-[#FD9800] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-400 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </BlogLayout>
  );
};
